import { useState, useEffect, useCallback } from 'react';

export type DraftSummary = {
  id: string;
  title: Record<string, string>;
  module_ids: string[];
  /**
   * Chosen modules with display names (resolved by survey-service, same
   * order as `module_ids`). The card renders these; `name` falls back to
   * the id for modules no longer published. May be absent on responses
   * served before the EL-489 backend rolled out — callers fall back to
   * `module_ids`.
   */
  modules?: { id: string; name: string }[];
  /**
   * Enriched company sector/industry (e.g. "Bakker & Patissier"). Rendered
   * as a pill on the board card when present. Not yet populated by the
   * backend — onboarding-state would need to surface the tenant's enriched
   * industry — so the row stays hidden until that lands (EL-489 follow-up).
   */
  sector?: string | null;
  languages: string[];
  participant_count: number;
  estimated_start_date: string | null;
  anonymity_threshold: number;
  first_insights_threshold: number;
  questions_count: number;
  questions_estimated_minutes: number;
  frameworks: string[];
};

export type OnboardingState = {
  is_onboarded: boolean;
  has_launched_survey: boolean;
  tenant: { email_verified: boolean };
  draft_survey: DraftSummary | null;
  steps: {
    survey_built: boolean;
    email_verified: boolean;
    upload_team: boolean;
    launch: boolean;
  };
  /**
   * Present and `true` when the BFF responded with a synthetic "fully
   * onboarded" response because `ONBOARDING_FLOW_ENABLED=false` (EL-678).
   * Absent (or `false`) on real backend-composed responses. Clients that only
   * gate on `is_onboarded` are unaffected — this field is additive.
   */
  onboarding_flow_disabled?: boolean;
};

/**
 * @param enabled When false, the hook does not fetch and stays in a
 *   not-loaded state (state=null, isLoading=false). Lets cross-persona
 *   callers (e.g. the shared sidebar) mount the hook unconditionally but
 *   only hit the onboarding-state endpoint for the insights persona.
 */
export function useOnboardingState(enabled: boolean = true) {
  const [state, setState] = useState<OnboardingState | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);

  const fetchState = useCallback(async () => {
    if (!enabled) {
      setState(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/insights/onboarding-state', { credentials: 'include' });
      if (!resp.ok) throw new Error(`onboarding-state ${resp.status}`);
      const data = (await resp.json()) as OnboardingState;
      setState(data);
    } catch (err) {
      setError(err as Error);
      setState(null);
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void fetchState();
  }, [fetchState]);

  return { state, isLoading, error, refetch: fetchState };
}
